import React, { Component } from "react";
import "../../App.css";
import { getPublicPositions, getCompleteOrders, getIndices } from "../../Api/Http";
import positionsConfig from "../../config/positions.json";
import ordersConfig from "../../config/orders.json";
import investedSummaryConfig from "../../config/investedSummary.json";
import { roundOff } from "../../Utils/utils";

const TRANSACTION_TYPE = { BUY: "BUY", SELL: "SELL" };

const sumBy = (list, pick) => list.reduce((total, item) => total + pick(item), 0);

// A squared-off position (quantity 0) carries no unrealized P&L from the backend (its
// quantity * price delta is always 0), so recompute the realized P&L from the matching
// BUY/SELL orders for that symbol. Multiple fills on either side are combined using their
// quantity-weighted average price rather than picking a single order.
const calculateSquaredOffPnl = ({ tradingsymbol, orders }) => {
    const symbolOrders = (orders || []).filter(order => order.tradingsymbol === tradingsymbol);
    const buyOrders = symbolOrders.filter(order => order.transactionType === TRANSACTION_TYPE.BUY);
    const sellOrders = symbolOrders.filter(order => order.transactionType === TRANSACTION_TYPE.SELL);
    if (!buyOrders.length || !sellOrders.length) return 0;

    const buyQuantity = sumBy(buyOrders, order => Number(order.quantity) || 0);
    const sellQuantity = sumBy(sellOrders, order => Number(order.quantity) || 0);
    if (!buyQuantity || !sellQuantity) return 0;

    const buyPrice =
        sumBy(buyOrders, order => (Number(order.quantity) || 0) * (Number(order.averagePrice) || 0)) / buyQuantity;
    const sellPrice =
        sumBy(sellOrders, order => (Number(order.quantity) || 0) * (Number(order.averagePrice) || 0)) / sellQuantity;
    const quantity = Math.min(buyQuantity, sellQuantity);

    return (sellPrice - buyPrice) * quantity;
};

// GET /api/public/positions groups rows by "SYMBOL-investedAmount" (one entry per day the
// position existed) instead of a flat list — flatten it into the rows this page renders.
const flattenPositions = (data, orders) => {
    if (!data) return [];
    return Object.keys(data).map(key => {
        const entries = data[key] || [];
        const entry = entries[entries.length - 1] || {};
        const tradingsymbol = key.replace(/-\d+$/, "");
        const averagePrice = Number(entry.averagePrice) || 0;
        const ltp = entry.lastPrice != null ? Number(entry.lastPrice) : averagePrice;
        const chgPercent = averagePrice ? roundOff(((ltp - averagePrice) / averagePrice) * 100) : 0;
        const quantity = Number(entry.quantity);
        const basePnl = entry.pnl != null ? Number(entry.pnl) : 0;
        const pnl = quantity === 0 ? Math.round(basePnl + calculateSquaredOffPnl({ tradingsymbol, orders })) : basePnl;
        return {
            tradingsymbol,
            quantity,
            averagePrice,
            ltp,
            pnl,
            chgPercent,
        };
    });
};

const PERCENT_KEYS = ["chgPercent", "totalPnlPercent", "niftyMovement"];
const COLORABLE_KEYS = ["pnl", "chgPercent", "totalPnl", "totalPnlPercent", "niftyMovement"];

const formatCellValue = (key, value) => {
    if (PERCENT_KEYS.includes(key) && value != null) {
        return `${value}%`;
    }
    if (key === "orderTimestamp" && value) {
        return value.split(" ")[1] || value;
    }
    return value != null ? value : "-";
};

// Optional Kite-style total row: label right-aligned up to `totalColumnKey`, value in that
// column, colored, remaining columns left blank.
const renderTotalRow = ({ columns, label, value, totalColumnKey }) => {
    const columnKeys = Object.keys(columns);
    const totalColumnIndex = columnKeys.indexOf(totalColumnKey);
    return (
        <tr>
            <td
                className="positions-orders-table-cell"
                colSpan={totalColumnIndex}
                style={{ textAlign: "right", fontWeight: 600 }}>
                {label}
            </td>
            <td className={"positions-orders-table-cell" + (value < 0 ? " value-negative" : " value-positive")}>
                {value}
            </td>
            <td className="positions-orders-table-cell" colSpan={columnKeys.length - totalColumnIndex - 1} />
        </tr>
    );
};

const renderTable = ({ columns, rows, rowKey, totalRow, className, renderExpanded }) => (
    <table className={className}>
        <tbody>
            <tr>
                {Object.keys(columns).map(key => (
                    <th key={key} className="positions-orders-table-heading">
                        {columns[key].name}
                    </th>
                ))}
            </tr>
            {rows.map(row => (
                <React.Fragment key={row[rowKey]}>
                    <tr>
                        {Object.keys(columns).map(key => (
                            <td
                                key={key}
                                className={
                                    "positions-orders-table-cell" +
                                    (key === "period" ? " positions-orders-table-cell-wrap" : "") +
                                    (COLORABLE_KEYS.includes(key) && row[key] < 0
                                        ? " value-negative"
                                        : COLORABLE_KEYS.includes(key) && row[key] > 0
                                          ? " value-positive"
                                          : "")
                                }>
                                {formatCellValue(key, row[key])}
                            </td>
                        ))}
                    </tr>
                    {renderExpanded && renderExpanded(row)}
                </React.Fragment>
            ))}
            {totalRow}
        </tbody>
    </table>
);

class PositionsOrders extends Component {
    toggleExpanded = period => {
        this.setState(prev => ({ expanded: prev.expanded === period ? null : period }));
    };

    // Same expand pattern as the Indices page's history column.
    renderListButton = (period, list) => (
        <button onClick={() => this.toggleExpanded(period)}>
            {`${this.state.expanded === period ? "\u25B2 Hide" : "\u25BC View"} (${list.length})`}
        </button>
    );

    renderInvestedList = row =>
        this.state.expanded === row.period && (
            <tr>
                <td
                    className="positions-orders-table-cell"
                    colSpan={Object.keys(investedSummaryConfig.page.columns).length}>
                    {row.positions.length
                        ? renderTable({
                              columns: investedSummaryConfig.page.listColumns,
                              rows: row.positions,
                              rowKey: "tradingsymbol",
                              className: "data-table positions-table",
                          })
                        : "No positions"}
                </td>
            </tr>
        );

    async componentDidMount() {
        const [positionsResp, ordersResp, indicesResp] = await Promise.all([
            getPublicPositions(),
            getCompleteOrders(),
            getIndices(),
        ]);
        const orders = (ordersResp && ordersResp.data) || [];
        const positions = flattenPositions(positionsResp && positionsResp.data, orders);
        // Sum the (realized-P&L-adjusted) rows rather than trusting the backend's conclusion.totalPnl,
        // which only accounts for unrealized P&L and is 0 for squared-off positions.
        const totalInvested = (positionsResp && positionsResp.conclusion && positionsResp.conclusion.total) || 0;
        const totalPnl = Math.round(sumBy(positions, position => Number(position.pnl) || 0));
        const totalPnlPercent = totalInvested ? Math.round((totalPnl / Math.abs(totalInvested)) * 100 * 100) / 100 : 0;
        this.setState({
            positions,
            totalInvested,
            totalPnl,
            totalPnlPercent,
            orders,
            nifty: indicesResp,
            marketStatus: indicesResp && indicesResp.marketStatus,
            invested: positionsResp && positionsResp.invested,
            date: positionsResp && positionsResp.conclusion && positionsResp.conclusion.from,
        });
    }

    render() {
        const { positions, totalInvested, totalPnl, totalPnlPercent, orders, nifty, marketStatus, invested, date } =
            this.state || {};
        const investedRows = [];
        if (invested && invested.lastWorkingDay) {
            const lastWorkingDay = invested.lastWorkingDay;
            investedRows.push({
                period: lastWorkingDay.from ? `Last Day\n(${lastWorkingDay.from})` : "Last Day",
                ...lastWorkingDay,
                positions: flattenPositions(lastWorkingDay.data, []),
            });
        }
        if (invested && invested.week) {
            const week = invested.week;
            investedRows.push({
                period: week.from && week.to ? `Last Week\n(${week.from} to ${week.to})` : "Last Week",
                ...week,
                positions: flattenPositions(week.data, []),
            });
        }
        for (const row of investedRows) {
            row.list = this.renderListButton(row.period, row.positions);
        }
        return (
            <div>
                <div className="market-header">
                    {nifty && nifty.price != null && (
                        <span className="market-header-item">
                            {nifty.name}: {roundOff(nifty.price)}{" "}
                            {nifty.changePercent != null && (
                                <span className={nifty.changePercent < 0 ? "value-negative" : "value-positive"}>
                                    ({roundOff(nifty.changePercent)}%)
                                </span>
                            )}
                            {(nifty.highestPercent != null || nifty.lowestPercent != null) && (
                                <span>
                                    {" "}
                                    [
                                    {nifty.highestPercent != null && (
                                        <span className="value-positive">H: {roundOff(nifty.highestPercent)}%</span>
                                    )}
                                    {nifty.highestPercent != null && nifty.lowestPercent != null && " "}
                                    {nifty.lowestPercent != null && (
                                        <span className="value-negative">L: {roundOff(nifty.lowestPercent)}%</span>
                                    )}
                                    ]
                                </span>
                            )}
                        </span>
                    )}
                    <span className="market-header-item">Total Invested: ₹{totalInvested || 0}</span>
                    <span className="market-header-item">
                        Net P&L:{" "}
                        <span className={totalPnl < 0 ? "value-negative" : "value-positive"}>
                            ₹{totalPnl || 0} ({totalPnlPercent || 0}%)
                        </span>
                    </span>
                    {marketStatus && (
                        <span className={marketStatus === "Market is open" ? "value-positive" : "market-status-banner"}>
                            {marketStatus}
                        </span>
                    )}
                    <span className="market-header-tagline">
                        Trades that manage themselves — entry to exit, no babysitting.
                    </span>
                </div>

                <p className="data-scope-note">Note: New Position will appear in ~2 mins in running market.</p>

                <header>
                    <h1>
                        Positions ({(positions && positions.length) || 0}){" "}
                        {date && <span className="h1-subtext">({date})</span>}
                    </h1>
                </header>
                <div className="triggers-table">
                    {positions &&
                        renderTable({
                            columns: positionsConfig.page.columns,
                            rows: positions,
                            rowKey: "tradingsymbol",
                            className: "data-table positions-table",
                            totalRow: positions.length
                                ? renderTotalRow({
                                      columns: positionsConfig.page.columns,
                                      label: "Total P&L",
                                      value: totalPnl || 0,
                                      totalColumnKey: "pnl",
                                  })
                                : null,
                        })}
                </div>

                <header>
                    <h1>
                        Orders ({(orders && orders.length) || 0}) {date && <span className="h1-subtext">({date})</span>}
                    </h1>
                </header>
                <div className="triggers-table">
                    {orders &&
                        renderTable({
                            columns: ordersConfig.page.columns,
                            rows: orders,
                            rowKey: "orderId",
                            className: "data-table orders-table",
                        })}
                </div>

                {investedRows.length > 0 && (
                    <>
                        <header>
                            <h1>
                                Invested Summary <span className="h1-subtext">(Past Period)</span>
                            </h1>
                        </header>
                        <div className="triggers-table">
                            {renderTable({
                                columns: investedSummaryConfig.page.columns,
                                rows: investedRows,
                                rowKey: "period",
                                className: "data-table invested-summary-table",
                                renderExpanded: this.renderInvestedList,
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export { flattenPositions, calculateSquaredOffPnl };
export default PositionsOrders;
